import { getDeviceDisplayName } from '@packages/types/app';
// --- React ---
import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';

// --- 다국어 ---
import i18n from 'i18next';

// --- 스토어 ---
import { useAuthStore } from '@/store/authStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useModalStore } from '@/store/modalStore';

// --- 훅 ---
import { useCompanySiteScope } from '@/hooks/useCompanySiteScope';
import { useMobileNavLayout } from '@/hooks/useMobileNavLayout';

// --- 공통 UI 컴포넌트 ---
import {
  DefaultContents,
  GoogleMap,
  type GoogleMapRef,
  DateRangePicker,
  type DateRange,
  Card,
} from '@/components/ui';
import { Resizer } from '@/components/common/Resizer';

// --- 타입 ---
import type { MapMarker, MapInfoWindowData } from '@/components/ui/common/map/types';
import {
  SystemStatusGroup,
  LITE_STATUS_GROUP_MAP,
  MINI_STATUS_GROUP_MAP,
  getHighestPriorityGroup,
} from '@packages/types/device/common/system_status_group';
import { BATTERY_STATE_MAP } from '@packages/types/device/lite/batteryState';

// --- API ---
import { useMonitoringData, usePushNotificationHistory } from '@/api/monitoring/useMonitoring';
import { useForceDisconnect } from '@/api/device/useDevice';

// --- 스타일 · 테마 ---
import { useTheme } from '@/theme';

// --- 유틸 ---
import { getMonitoringMarkerColor } from '@/utils/monitoringGroupColors';
import { getDeviceImageUrl } from '@/utils/deviceImageUtils';
import { parseMonitoringApiError } from '@/utils/errors';

// --- 페이지 컴포넌트 ---
import { MonitoringDeviceList } from './MonitoringDeviceList';
import { PushNotificationHistoryGrid } from './PushNotificationHistoryGrid';

// ==================== 컴포넌트 ====================

/**
 * Monitoring Page 컴포넌트
 *
 * 권한별 기능:
 * - SuperAdmin/Admin: Company 선택, Site 선택, 전체 기기 보기
 * - Company Manager: Site 선택, Company 전체 기기 보기
 * - Site Manager: 본인 사이트 기기만
 */
const MonitoringPage: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const { theme } = useTheme();
  // 모바일(<1024px)에서는 분할 리사이저 레이아웃 대신 세로로 쌓아 스크롤되게 한다.
  const isMobile = useMobileNavLayout();
  const { selectedCompanyId, selectedSiteId } = useSelectionStore();
  const {
    companies,
    currentCompany,
    sites,
    currentSiteId,
    isAdmin,
    isCompanyManager,
    isSiteManager,
  } = useCompanySiteScope();

  // ==================== Mutations ====================

  const forceDisconnectMutation = useForceDisconnect();
  const { showConfirm } = useModalStore();

  const handleForceDisconnect = useCallback(
    (serialNumber: string) => {
      showConfirm(
        '연결 강제 해제',
        `${serialNumber} 기기의 연결을 강제 해제하시겠습니까?`,
        () => {
          forceDisconnectMutation.mutate(serialNumber);
        },
        undefined,
        'danger'
      );
    },
    [forceDisconnectMutation, showConfirm]
  );

  // ==================== 상태 관리 ====================

  const [isConnectedFilter, setIsConnectedFilter] = useState<boolean | 'all'>('all');

  // 상단 영역 높이 (지도+Push History)
  const [topAreaHeight, setTopAreaHeight] = useState(500);
  // 지도 너비 (드래그로 조절 가능)
  const [mapWidth, setMapWidth] = useState(50); // 퍼센트
  // 드래그 상태
  const [isDragging, setIsDragging] = useState(false);
  const [isTopAreaDragging, setIsTopAreaDragging] = useState(false);
  const [isMapWidthDragging, setIsMapWidthDragging] = useState(false);
  // 지도 ref
  const mapRef = useRef<GoogleMapRef>(null);
  // 컨테이너 ref (높이 계산용)
  const containerRef = useRef<HTMLDivElement>(null);
  // 현재 위치 상태
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  // Push Alarm 확장 뷰 상태
  const [isPushExpanded, setIsPushExpanded] = useState(false);
  const [expandedDateRange, setExpandedDateRange] = useState<DateRange>(() => {
    const today = new Date();
    const monthAgo = new Date(today);
    monthAgo.setMonth(today.getMonth() - 1);
    return {
      from: monthAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0],
    };
  });

  // ==================== API 훅 ====================

  const effectiveSiteId = isSiteManager
    ? currentSiteId
    : selectedSiteId === 'all' || selectedSiteId === null
      ? null
      : selectedSiteId;

  /**
   * 조회 가능 여부 확인.
   * UX: SelectBox는 첫 번째 옵션이 선택되어 보이지만,
   * 실제 조회는 유효한 값이 선택되어야만 실행.
   */
  const canFetchMonitoringData = useMemo(() => {
    if (!isAuthenticated) return false;

    // 역할 체크는 구체적인 것부터 (계층형 role 함수이므로 순서가 중요)
    // Admin이 isCompanyManager/isSiteManager 모두 true로 평가되므로 Admin을 먼저 체크
    if (isAdmin) {
      const hasValidCompany = selectedCompanyId !== null && selectedCompanyId !== 'all';
      return hasValidCompany;
    }

    // CompanyManager (role_id=2): company가 로드되고 사이트가 하나라도 있으면 조회 가능
    if (isCompanyManager) {
      return !!currentCompany?.id && sites.length > 0;
    }

    // SiteManager (role_id=3): 담당 사이트가 하나라도 있으면 조회 가능
    if (isSiteManager) return sites.length > 0;

    return false;
  }, [
    isAuthenticated,
    isAdmin,
    isCompanyManager,
    isSiteManager,
    selectedCompanyId,
    currentCompany,
    sites,
  ]);

  const {
    data: monitoringData = [],
    isLoading,
    error,
    dataUpdatedAt,
  } = useMonitoringData({
    companyId: isAdmin
      ? selectedCompanyId === 'all' || selectedCompanyId === null
        ? null
        : selectedCompanyId
      : currentCompany?.id || null,
    siteId: effectiveSiteId,
    isConnected: isConnectedFilter === 'all' ? null : isConnectedFilter,
    enabled: canFetchMonitoringData,
  });

  // Push Notification History 조회
  const {
    data: pushHistoryDataRaw = [],
    isLoading: isLoadingPushHistory,
    error: pushHistoryError,
  } = usePushNotificationHistory({
    companyId: isAdmin
      ? selectedCompanyId === 'all' || selectedCompanyId === null
        ? null
        : selectedCompanyId
      : currentCompany?.id || null,
    siteId: effectiveSiteId,
    enabled: canFetchMonitoringData,
  });

  // 최근 100개만 표시 (compact 뷰용)
  const pushHistoryData = useMemo(() => {
    return pushHistoryDataRaw.slice(0, 100);
  }, [pushHistoryDataRaw]);

  // Push History 확장 뷰용 (날짜 범위 기반)
  const expandedCompanyId = isAdmin
    ? selectedCompanyId === 'all' || selectedCompanyId === null
      ? null
      : selectedCompanyId
    : currentCompany?.id || null;

  const {
    data: expandedPushData = [],
    isLoading: isLoadingExpandedPush,
    error: expandedPushError,
  } = usePushNotificationHistory({
    companyId: expandedCompanyId,
    siteId: effectiveSiteId,
    startDate: expandedDateRange.from || null,
    endDate: expandedDateRange.to || null,
    enabled: canFetchMonitoringData && isPushExpanded,
  });

  // ==================== 파생 데이터 ====================

  const monitoringQueryError = useMemo(
    () => (error ? parseMonitoringApiError(error) : null),
    [error]
  );

  const activePushError = isPushExpanded ? expandedPushError : pushHistoryError;
  const pushQueryError = useMemo(
    () => (activePushError ? parseMonitoringApiError(activePushError) : null),
    [activePushError]
  );

  /**
   * 모니터링 데이터 → 가공된 디바이스 리스트 + 지도 마커 + 정보창 변환.
   * 1) 센서 데이터 매핑 및 필터링
   * 2) 연결 상태 → 시스템 상태 그룹 순으로 정렬
   * 3) 지도 마커 및 InfoWindow 데이터 생성
   */
  const { processedData, markers, infoWindowData } = useMemo(() => {
    const processed = monitoringData.map(item => {
      // 백엔드에서 변환된 sensors 객체를 직접 사용
      // sensors는 센서명을 키로 하는 객체: { "O2": { ... }, "CO": { ... }, "EX": { ... } }
      const sensors = item.monitoring?.sensors || {};

      // information의 sensor_type 배열과 매칭하여 decimal_point 찾기
      const sensorTypes = item.information?.sensor_type || [];
      const decimalPoints = item.information?.decimal_point || [];

      // sensors 객체를 배열로 변환 (기존 mappedSensors 형식 유지)
      const mappedSensors = Object.entries(sensors)
        .map(([name, sensorData]: [string, any]) => {
          // sensor_type으로 인덱스 찾기
          const sensorType = sensorData.sensor_type;
          const sensorIndex = sensorTypes.findIndex((type: number) => type === sensorType);
          const decimalPoint =
            sensorIndex >= 0 && decimalPoints[sensorIndex] !== undefined
              ? decimalPoints[sensorIndex]
              : 0;

          return {
            name,
            gasConc: sensorData.gas_conc,
            unit: sensorData.conc_unit || 'PPM',
            decimalPoint,
            status: Array.isArray(sensorData.status_name)
              ? sensorData.status_name
              : sensorData.status_name
                ? [sensorData.status_name]
                : ['Normal'],
            warning:
              sensorData.warning_name && sensorData.warning_name !== 'No Warning'
                ? sensorData.warning_name
                : null,
            error:
              sensorData.error_name && sensorData.error_name !== 'No Error'
                ? sensorData.error_name
                : null,
          };
        })
        .filter(sensor => {
          // 센서 이름이 유효한지 확인
          if (!sensor.name || sensor.name.trim() === '') return false;
          // 'None', 'No Sensor', 'N/A' 등 무효한 센서 제외
          const invalidNames = ['none', 'no sensor', 'n/a', 'null', 'undefined'];
          if (invalidNames.includes(sensor.name.toLowerCase().trim())) return false;
          // Sensor Disabled 상태인 센서 제외
          if (sensor.status.some((s: string) => s === 'Sensor Disabled')) return false;
          return true;
        });

      // 디바이스 이미지 URL 계산 (한 번만, 모든 곳에서 재사용)
      const deviceImageUrl = getDeviceImageUrl(item.device_type, {
        appId: item.device.app_id,
        equippedSensor: item.information?.sensor_type?.[0] ?? null,
        sensorTypes: item.device.sensor_types,
        isConnected: item.device.is_connected,
      });

      return {
        ...item,
        mappedSensors,
        deviceImageUrl,
      };
    });

    // 미연결 기기 필터링:
    // - 연결된 기기는 유지
    // - 미연결 기기는 last_connect가 1시간 이내일 때만 유지 (그 외/없음은 제거)
    const ONE_HOUR_MS = 60 * 60 * 1000;
    const now = Date.now();

    const visible = processed.filter(item => {
      if (item.device.is_connected) return true;
      const lc = item.device.last_connect;
      if (!lc) return false; // last_connect 없음 → 제거
      const t = new Date(lc).getTime();
      if (Number.isNaN(t)) return false;
      return now - t <= ONE_HOUR_MS; // 1시간 이내만 유지
    });

    // 정렬: 1) 연결된 기기가 위로, 2) 연결된 기기 그 후에는 system status로 정렬
    const sorted = visible.sort((a, b) => {
      const isConnectedA = a.device.is_connected || false;
      const isConnectedB = b.device.is_connected || false;

      // 1. 연결 상태로 먼저 정렬 (연결된 기기가 위로)
      if (isConnectedA !== isConnectedB) {
        return isConnectedA ? -1 : 1;
      }

      // 2. 같은 연결 상태 내에서 System Status 그룹으로 정렬
      const getSystemStatusGroup = (item: typeof a): SystemStatusGroup => {
        const systemStatusLabels = item.monitoring.system_status_name || [];

        const statusGroupMap =
          item.device_type === 'bellmini' ? MINI_STATUS_GROUP_MAP : LITE_STATUS_GROUP_MAP;

        return getHighestPriorityGroup(systemStatusLabels, statusGroupMap);
      };

      const groupA = getSystemStatusGroup(a);
      const groupB = getSystemStatusGroup(b);

      // 그룹 우선순위: GroupD > GroupB > GroupC > GroupA
      const groupPriority: Record<SystemStatusGroup, number> = {
        [SystemStatusGroup.GroupD]: 4,
        [SystemStatusGroup.GroupB]: 3,
        [SystemStatusGroup.GroupC]: 2,
        [SystemStatusGroup.GroupA]: 1,
      };

      const priorityA = groupPriority[groupA] || 0;
      const priorityB = groupPriority[groupB] || 0;

      // 우선순위가 높은 것(숫자가 큰 것)이 위로
      if (priorityA !== priorityB) {
        return priorityB - priorityA;
      }

      // 그룹이 같으면 last_connect 기준으로 정렬 (최신순)
      const lastConnectA = a.device.last_connect;
      const lastConnectB = b.device.last_connect;

      if (!lastConnectA && !lastConnectB) return 0;
      if (!lastConnectA) return 1;
      if (!lastConnectB) return -1;

      try {
        const dateA = new Date(lastConnectA);
        const dateB = new Date(lastConnectB);

        if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
        if (isNaN(dateA.getTime())) return 1;
        if (isNaN(dateB.getTime())) return -1;

        return dateB.getTime() - dateA.getTime();
      } catch {
        return 0;
      }
    });

    // 마커 및 정보창 데이터 생성
    const markersArray: MapMarker[] = [];
    const infoWindowMap = new Map<string | number, MapInfoWindowData>();

    sorted.forEach(item => {
      const { monitoring, device, mappedSensors } = item;

      // 고정 GPS 값이 있으면 우선 사용, 없으면 모니터링 데이터 사용
      const latitude =
        device.fixed_latitude !== null && device.fixed_latitude !== undefined
          ? device.fixed_latitude
          : monitoring.latitude;
      const longitude =
        device.fixed_longitude !== null && device.fixed_longitude !== undefined
          ? device.fixed_longitude
          : monitoring.longitude;

      // 위치 정보가 없으면 마커 생성 불가
      if (!latitude || !longitude) return;

      const deviceName = device.cloud_name || device.cloud_user || device.serial_number;
      const isConnected = device.is_connected || false;

      // System Status 확인 (미연결 기기는 'Disconnected'로 표시)
      const systemStatusName = !isConnected
        ? 'Disconnected'
        : Array.isArray(monitoring.system_status_name) && monitoring.system_status_name.length > 0
          ? monitoring.system_status_name[0]
          : 'Normal';

      // System Status 그룹 계산
      const systemStatusLabels = monitoring.system_status_name || [];

      const statusGroupMap =
        item.device_type === 'bellmini' ? MINI_STATUS_GROUP_MAP : LITE_STATUS_GROUP_MAP;

      const systemStatusGroup = getHighestPriorityGroup(systemStatusLabels, statusGroupMap);

      // 마커 색상: 연결되었을 경우 system status 기준 그룹 컬러, 연결 안되었을 경우 회색
      const markerColor = isConnected
        ? getMonitoringMarkerColor(systemStatusGroup, theme.colors)
        : '#6b7280'; // 회색

      // 마커 아이콘 (단일 색상)
      const markerIcon = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12,2a8,8,0,0,0-8,8c0,5.4,7,11.5,7.35,11.76l.65.56.65-.56C13,21.5,20,15.4,20,10A8,8,0,0,0,12,2Zm0,12a4,4,0,1,1,4-4A4,4,0,0,1,12,14Z" fill="${markerColor}"/>
              <circle cx="12" cy="10" r="2" fill="${markerColor}"/>
            </svg>
          `)}`;

      const marker: MapMarker = {
        id: device.serial_number,
        latitude: latitude,
        longitude: longitude,
        title: deviceName,
        description: `${getDeviceDisplayName(item.device_type, item.device.app_id)} - ${systemStatusName}`,
        color: markerColor,
        icon: markerIcon,
        data: {
          ...monitoring,
          isConnected,
          systemStatus: systemStatusName,
          sensorStatuses: mappedSensors.map(s => s.status).join(','),
          deviceImageUrl: item.deviceImageUrl,
        },
      };

      markersArray.push(marker);

      // 정보창 데이터 생성
      const sensors = mappedSensors.map(sensor => ({
        type: sensor.name,
        concentration:
          sensor.gasConc !== undefined && sensor.gasConc !== null
            ? `${Number(sensor.gasConc).toFixed(sensor.decimalPoint)} ${sensor.unit}`
            : '--',
        status: sensor.status,
        warning: sensor.warning && sensor.warning !== 'No Warning' ? sensor.warning : null,
        error: sensor.error && sensor.error !== 'No Error' ? sensor.error : null,
      }));

      infoWindowMap.set(device.serial_number, {
        name: deviceName,
        serialNumber: device.serial_number,
        deviceType: getDeviceDisplayName(item.device_type, item.device.app_id),
        systemStatus: systemStatusName,
        sensors: sensors.map(s => ({
          type: s.type,
          concentration: s.concentration,
          status: s.status,
          warning:
            typeof s.warning === 'string'
              ? s.warning
              : Array.isArray(s.warning)
                ? s.warning.join(', ')
                : null,
          error:
            typeof s.error === 'string'
              ? s.error
              : Array.isArray(s.error)
                ? s.error.join(', ')
                : null,
        })),
        bumpDueDate: device.bump_test_due_date || undefined,
        calDueDate: device.calibration_due_date || undefined,
        timestamp: monitoring.timestamp ? String(monitoring.timestamp) : undefined,
        temperature:
          monitoring.temperature !== undefined && monitoring.temperature !== null
            ? `${Number(monitoring.temperature).toFixed(1)}°C`
            : undefined,
        batteryState:
          monitoring.battery_state !== undefined && monitoring.battery_state !== null
            ? BATTERY_STATE_MAP[monitoring.battery_state] || `Unknown (${monitoring.battery_state})`
            : undefined,
        batteryStateValue:
          monitoring.battery_state !== undefined && monitoring.battery_state !== null
            ? monitoring.battery_state
            : undefined,
        deviceImageUrl: item.deviceImageUrl,
        additionalInfo: {
          sensorDetails: sensors.map((s, idx) => ({
            index: idx,
            statusDetails: [s.status],
          })),
        },
      });
    });

    return {
      processedData: sorted,
      markers: markersArray,
      infoWindowData: infoWindowMap,
    };
  }, [monitoringData]);

  // ==================== 이펙트 ====================

  /**
   * 현재 위치 가져오기 (컴포넌트 마운트 시 한 번만 실행).
   * 위치를 가져오지 못해도 기본값(서울)으로 계속 진행.
   */
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      position => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      error => {
        console.error('Error getting user location:', error);
        // 위치를 가져오지 못해도 계속 진행 (기본값 사용)
      },
      {
        enableHighAccuracy: false, // 빠른 응답을 위해 false
        timeout: 5000, // 5초 타임아웃
        maximumAge: 60000, // 1분간 캐시된 위치 사용 가능
      }
    );
  }, []);

  /**
   * 지도 중심 좌표 계산 — 가장 최근 접속된 기기의 위치로 설정.
   * 마커가 없으면 현재 위치 또는 서울 기본값 사용.
   */
  const mapCenter = useMemo(() => {
    if (markers.length === 0) {
      return userLocation || { lat: 37.5664, lng: 126.978 }; // 서울 기본값
    }

    // 가장 최근 접속된 기기 찾기
    const devicesWithLastConnect = processedData
      .filter(data => {
        const hasLastConnect =
          data.device.last_connect !== null && data.device.last_connect !== undefined;
        const marker = markers.find(m => m.id === data.device.serial_number);
        const hasLocation = marker && marker.latitude && marker.longitude;
        return hasLastConnect && hasLocation;
      })
      .map(data => ({
        data,
        lastConnect: new Date(data.device.last_connect as string).getTime(),
        marker: markers.find(m => m.id === data.device.serial_number)!,
      }))
      .sort((a, b) => b.lastConnect - a.lastConnect); // 최신순 정렬

    // 가장 최근 접속된 기기가 있으면 그 위치 사용
    if (devicesWithLastConnect.length > 0) {
      const mostRecentDevice = devicesWithLastConnect[0];
      return {
        lat: mostRecentDevice.marker.latitude,
        lng: mostRecentDevice.marker.longitude,
      };
    }

    // 최근 접속된 기기가 없으면 모든 마커의 평균 위치 사용
    const avgLat = markers.reduce((sum, m) => sum + m.latitude, 0) / markers.length;
    const avgLng = markers.reduce((sum, m) => sum + m.longitude, 0) / markers.length;

    return { lat: avgLat, lng: avgLng };
  }, [markers, userLocation, processedData]);

  // ==================== 핸들러 ====================

  /** 연결 필터 변경 */
  const handleConnectionFilterChange = (isConnected: boolean | 'all') => {
    setIsConnectedFilter(isConnected);
  };

  /** 리스트에서 기기 클릭 시 지도 해당 마커로 이동 */
  const handleDeviceClick = (serialNumber: string) => {
    if (mapRef.current) {
      mapRef.current.panToMarker(serialNumber);
    } else {
      console.warn('[MonitoringPage] mapRef.current is null');
    }
  };

  /** Push 알람 리스트에서 클릭 시 지도 해당 마커로 이동 */
  const handlePushListClick = (serialNumber: string) => {
    if (mapRef.current) {
      mapRef.current.panToMarker(serialNumber);
    } else {
      console.warn('[MonitoringPage] mapRef.current is null');
    }
  };

  // ── 리사이저: 하단 리스트 높이 조절 ──

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerTop = containerRect.top;
      const containerHeight = containerRect.height;

      const mouseY = e.clientY;
      const relativeY = mouseY - containerTop;

      const newTopAreaHeight = relativeY;

      const minTopAreaHeight = 200;
      const minListHeight = 200;
      const maxTopAreaHeight = containerHeight - minListHeight;

      const clampedTopAreaHeight = Math.max(
        minTopAreaHeight,
        Math.min(maxTopAreaHeight, newTopAreaHeight)
      );
      setTopAreaHeight(clampedTopAreaHeight);
    },
    [isDragging]
  );

  // ── 리사이저: 상단 영역 높이 조절 ──

  const handleTopAreaMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isTopAreaDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerTop = containerRect.top;
      const containerHeight = containerRect.height;

      const mouseY = e.clientY;
      const relativeY = mouseY - containerTop;

      const minTopAreaHeight = 200;
      const minListHeight = 200;
      const maxTopAreaHeight = containerHeight - minListHeight;

      const clampedTopAreaHeight = Math.max(
        minTopAreaHeight,
        Math.min(maxTopAreaHeight, relativeY)
      );
      setTopAreaHeight(clampedTopAreaHeight);
    },
    [isTopAreaDragging]
  );

  const handleTopAreaMouseUp = useCallback(() => {
    setIsTopAreaDragging(false);
  }, []);

  // ── 리사이저: 지도 너비 조절 ──

  const handleMapWidthMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsMapWidthDragging(true);
  }, []);

  const handleMapWidthMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isMapWidthDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerLeft = containerRect.left;
      const containerWidth = containerRect.width;

      const mouseX = e.clientX;
      const relativeX = mouseX - containerLeft;

      const mapWidthPercent = (relativeX / containerWidth) * 100;

      const minMapWidth = 20;
      const maxMapWidth = 80;

      const clampedMapWidth = Math.max(minMapWidth, Math.min(maxMapWidth, mapWidthPercent));
      setMapWidth(clampedMapWidth);
    },
    [isMapWidthDragging]
  );

  const handleMapWidthMouseUp = useCallback(() => {
    setIsMapWidthDragging(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // ── 마우스 이벤트 리스너 등록/해제 (하단 리스트 리사이저) ──
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // ── 마우스 이벤트 리스너 등록/해제 (상단 영역 리사이저) ──
  useEffect(() => {
    if (isTopAreaDragging) {
      document.addEventListener('mousemove', handleTopAreaMouseMove);
      document.addEventListener('mouseup', handleTopAreaMouseUp);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleTopAreaMouseMove);
      document.removeEventListener('mouseup', handleTopAreaMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleTopAreaMouseMove);
      document.removeEventListener('mouseup', handleTopAreaMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isTopAreaDragging, handleTopAreaMouseMove, handleTopAreaMouseUp]);

  // ── 마우스 이벤트 리스너 등록/해제 (지도 너비 리사이저) ──
  useEffect(() => {
    if (isMapWidthDragging) {
      document.addEventListener('mousemove', handleMapWidthMouseMove);
      document.addEventListener('mouseup', handleMapWidthMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMapWidthMouseMove);
      document.removeEventListener('mouseup', handleMapWidthMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMapWidthMouseMove);
      document.removeEventListener('mouseup', handleMapWidthMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isMapWidthDragging, handleMapWidthMouseMove, handleMapWidthMouseUp]);

  // ==================== 렌더링 ====================

  if (!isAuthenticated) {
    return <div>로그인이 필요합니다.</div>;
  }

  return (
    <>
      <DefaultContents>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: isMobile ? '0 0 auto' : 1,
            minHeight: 0,
            overflow: isMobile ? 'visible' : 'hidden',
            position: 'relative',
          }}
        >
          {/* 지도 및 Push History 영역 */}
          <div
            ref={containerRef}
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: isMobile ? '0 0 auto' : 1,
              minHeight: 0,
              overflow: isMobile ? 'visible' : 'hidden',
            }}
          >
            {/* 지도 및 Push History (데스크톱: 가로 배치 / 모바일: 세로 스택) */}
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                flexShrink: 0,
                height: isMobile ? 'auto' : `${topAreaHeight}px`,
                minHeight: isMobile ? undefined : '200px',
                gap: isMobile ? theme.spacing[4] : 0,
                overflow: isMobile ? 'visible' : 'hidden',
              }}
            >
              {/* 지도 영역 (왼쪽 / 모바일 상단) */}
              <div
                data-tour="monitoring-map"
                style={{
                  flex: isMobile ? '0 0 auto' : `0 0 calc(${mapWidth}% - 2px)`,
                  width: isMobile ? '100%' : undefined,
                  minWidth: isMobile ? 0 : '300px',
                  height: isMobile ? '360px' : undefined,
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Card
                  title="Monitoring Map"
                  padding={4}
                  shadow="md"
                  radius="lg"
                  layout="flex"
                  gap={2}
                  fullWidth
                  fullHeight
                >
                  {/* 지도 컨텐츠 */}
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflow: 'auto',
                      paddingTop: theme.spacing[1],
                      paddingLeft: theme.spacing[1],
                      paddingRight: theme.spacing[1],
                      paddingBottom: 0,
                    }}
                  >
                    <div
                      style={{
                        position: 'relative',
                        minHeight: '200px',
                        height: '100%',
                      }}
                    >
                      {isLoading && <div>Loading...</div>}
                      {monitoringQueryError && (
                        <div
                          style={{
                            color: theme.colors.status.danger.stroke.text,
                            padding: theme.spacing[2],
                            fontSize: theme.fontSize.sm,
                          }}
                        >
                          {i18n.t(monitoringQueryError.title)}
                          {monitoringQueryError.message &&
                          monitoringQueryError.message !== monitoringQueryError.title
                            ? ` — ${i18n.t(monitoringQueryError.message)}`
                            : ''}
                        </div>
                      )}
                      {!isLoading && !error && (
                        <GoogleMap
                          ref={mapRef}
                          center={mapCenter}
                          zoom={markers.length > 0 ? 17 : 17}
                          markers={markers}
                          infoWindowData={infoWindowData}
                          height="100%"
                          width="100%"
                        />
                      )}
                    </div>
                  </div>
                </Card>
              </div>

              {/* 지도와 Push History 사이 리사이저 (데스크톱 전용) */}
              {!isMobile && (
                <Resizer
                  direction="vertical"
                  onMouseDown={handleMapWidthMouseDown}
                  isDragging={isMapWidthDragging}
                />
              )}

              {/* Push Notification History 그리드 영역 (오른쪽 / 모바일 하단) */}
              <div
                data-tour="monitoring-push"
                style={{
                  flex: isMobile ? '0 0 auto' : 1,
                  width: isMobile ? '100%' : undefined,
                  minWidth: 0,
                  height: isMobile ? '420px' : undefined,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <Card
                  padding={4}
                  shadow="md"
                  radius="lg"
                  layout="flex"
                  gap={2}
                  fullWidth
                  fullHeight
                >
                  {/* 헤더: 타이틀 + 토글 버튼 */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: theme.fontSize.lg,
                        fontWeight: theme.fontWeight.semibold,
                        color: theme.colors.text.primary,
                      }}
                    >
                      Push Alarm
                    </span>
                    <button
                      type="button"
                      data-tour="monitoring-push-more"
                      onClick={() => setIsPushExpanded(v => !v)}
                      style={{
                        background: isPushExpanded
                          ? theme.colors.surface.interactive.hover
                          : 'transparent',
                        border: `1px solid ${theme.colors.border.divider}`,
                        borderRadius: theme.borderRadius.sm,
                        cursor: 'pointer',
                        padding: `${theme.spacing[1]}px ${theme.spacing[2]}px`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing[1],
                        color: isPushExpanded
                          ? theme.colors.text.primary
                          : theme.colors.text.secondary,
                        fontSize: theme.fontSize.sm,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {isPushExpanded ? (
                        <>
                          {/* 뒤로 화살표 아이콘 */}
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="15 18 9 12 15 6" />
                          </svg>
                          Back
                        </>
                      ) : (
                        <>
                          More
                          {/* 오른쪽 화살표 아이콘 */}
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </>
                      )}
                    </button>
                  </div>

                  {/* 확장 뷰: 날짜 범위 선택기 */}
                  {isPushExpanded && (
                    <div style={{ flexShrink: 0 }}>
                      <DateRangePicker
                        value={expandedDateRange}
                        onChange={setExpandedDateRange}
                        size="sm"
                        labelSize="xs"
                        width="auto"
                      />
                    </div>
                  )}

                  {/* Push History 그리드 */}
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      paddingTop: theme.spacing[1],
                      paddingLeft: theme.spacing[1],
                      paddingRight: theme.spacing[1],
                      paddingBottom: 0,
                    }}
                  >
                    {pushQueryError && (
                      <div
                        style={{
                          color: theme.colors.status.danger.stroke.text,
                          fontSize: theme.fontSize.sm,
                          marginBottom: theme.spacing[2],
                          flexShrink: 0,
                        }}
                      >
                        {i18n.t(pushQueryError.title)}
                        {pushQueryError.message && pushQueryError.message !== pushQueryError.title
                          ? ` — ${i18n.t(pushQueryError.message)}`
                          : ''}
                      </div>
                    )}
                    {isPushExpanded ? (
                      <PushNotificationHistoryGrid
                        data={expandedPushData}
                        isLoading={isLoadingExpandedPush}
                        enableFiltering={true}
                        enablePagination={true}
                        onRowClick={handlePushListClick}
                      />
                    ) : (
                      <PushNotificationHistoryGrid
                        data={pushHistoryData}
                        isLoading={isLoadingPushHistory}
                        enableFiltering={false}
                        enablePagination={false}
                        onRowClick={handlePushListClick}
                      />
                    )}
                  </div>
                </Card>
              </div>
            </div>

            {/* 드래그 가능한 구분선 (데스크톱 전용) */}
            {!isMobile && (
              <Resizer
                direction="horizontal"
                onMouseDown={handleMouseDown}
                isDragging={isDragging}
              />
            )}

            {/* 기기 리스트 영역 (하단) */}
            <div
              data-tour="monitoring-device-list"
              style={{
                flex: isMobile ? '0 0 auto' : 1,
                minHeight: isMobile ? undefined : '200px',
                height: isMobile ? '560px' : undefined,
                marginTop: isMobile ? theme.spacing[4] : undefined,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <MonitoringDeviceList
                dataUpdatedAt={dataUpdatedAt}
                onDeviceClick={handleDeviceClick}
                monitoringData={processedData}
                sites={sites}
                companies={companies}
                isLoading={isLoading}
                isConnectedFilter={isConnectedFilter}
                onConnectionFilterChange={handleConnectionFilterChange}
                isAdmin={isAdmin}
                onForceDisconnect={handleForceDisconnect}
              />
            </div>
          </div>
        </div>
      </DefaultContents>
    </>
  );
};

export default MonitoringPage;
